import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from '../AssignMentorDialog';
import { Spinner, Empty } from './shared';

const REFERRAL_PILL = {
  submitted: 'bg-violet-50 text-violet-600', invited: 'bg-blue-50 text-blue-600',
  confirmed: 'bg-sky-50 text-sky-700', approved: 'bg-emerald-50 text-emerald-600',
  rejected: 'bg-gray-100 text-gray-500', changes_requested: 'bg-amber-50 text-amber-700',
};
const REFERRAL_LABEL = {
  submitted: 'Needs review', invited: 'Representative invited', confirmed: 'Confirmed by company',
  approved: 'Approved', rejected: 'Declined', changes_requested: 'More info asked',
};
const PENDING = ['submitted', 'changes_requested'];

/** Internships proposed by parents or mentors.
 *  `unassigned` lists school-level leads with no programme; `embedded` shows only the
 *  proposals still waiting on the coordinator, inside the programme's Offers list. */
export default function ReferralsList({ programId, unassigned = false, embedded = false,
  programs = [], onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/coordinator/internship-referrals', {
        params: unassigned ? { unassigned: true } : { program_id: programId },
      });
      setRows(r.data || []);
    } catch { setRows([]); }
    setLoading(false);
  }, [programId, unassigned]);
  useEffect(() => { load(); }, [load]);

  const visible = embedded ? rows.filter(r => PENDING.includes(r.status)) : rows;
  const shown = filter === 'all' ? visible : visible.filter(r => r.status === filter);

  if (loading) return embedded ? null : <Spinner />;
  if (embedded && visible.length === 0) return null;

  const list = (
    <div className="space-y-2">
      {shown.map(r => (
        <div key={r.id} className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3" data-testid={`referral-row-${r.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-[#1F2937]">{r.company_name}</p>
                <Badge className={`border-0 text-[10px] ${REFERRAL_PILL[r.status]}`} data-testid={`referral-status-${r.id}`}>
                  {REFERRAL_LABEL[r.status] || r.status}
                </Badge>
                <Badge className="border-0 text-[10px] bg-[#FEF0EC] text-[#F06651]" data-testid={`referral-source-${r.id}`}>
                  Proposed by {r.parent_name}
                </Badge>
                <Badge className="border-0 text-[10px] bg-[#F3F4F6] text-[#6B7280]" data-testid={`referral-program-${r.id}`}>
                  {r.program_name || 'No programme yet'}
                </Badge>
              </div>
              <p className="text-sm text-[#6B7280] mt-0.5">{r.title}</p>
              <div className="mt-1.5 space-y-0.5 text-[11px] text-[#4B5563]">
                <p>Representative: {r.rep_name} · {r.rep_email}</p>
                {r.relationship && <p>Parent's connection: {r.relationship}</p>}
                {(r.start_date || r.positions) && (
                  <p>{r.start_date ? `${formatDay(r.start_date)} – ${formatDay(r.end_date)}` : 'Dates to confirm'}
                    {r.positions ? ` · ${r.positions} place${r.positions === 1 ? '' : 's'}` : ''}
                    {r.eligible_grades?.length ? ` · grades ${r.eligible_grades.join(', ')}` : ''}</p>
                )}
                {r.description && <p className="text-[#6B7280] leading-relaxed">{r.description}</p>}
                {r.notes && <p className="text-[#9CA3AF]">Parent's note: “{r.notes}”</p>}
                {r.coordinator_note && <p className="text-amber-700">Your note: “{r.coordinator_note}”</p>}
                {r.invitation_status && <p className="text-[#9CA3AF]">Invitation: {r.invitation_status}{r.offer_status ? ` · offer ${String(r.offer_status).replace(/_/g, ' ')}` : ''}</p>}
              </div>
            </div>
            {PENDING.includes(r.status) ? (
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button size="sm" className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-xs" onClick={() => setActing({ referral: r, decision: 'invite' })} data-testid={`invite-referral-${r.id}`}>
                  <Mail className="h-3.5 w-3.5 mr-1" />{r.program_id ? 'Invite representative' : 'Assign & invite'}
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg text-xs border-gray-200" onClick={() => setActing({ referral: r, decision: 'request_changes' })} data-testid={`ask-referral-${r.id}`}>Ask for more</Button>
                <Button size="sm" variant="ghost" className="rounded-lg text-xs text-red-500 hover:bg-red-50" onClick={() => setActing({ referral: r, decision: 'reject' })} data-testid={`reject-referral-${r.id}`}>Decline</Button>
              </div>
            ) : (
              <span className="text-[11px] text-[#9CA3AF] shrink-0">
                {r.status === 'invited' ? 'Waiting for the representative' : 'No action needed'}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const dialog = acting && (
    <ReferralDialog {...acting} programs={programs} onClose={() => setActing(null)}
      onDone={() => { setActing(null); load(); onChanged?.(); }} />
  );

  if (embedded) {
    return (
      <div className="mb-4" data-testid="referrals-embedded">
        <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2">
          Proposals waiting on you
        </p>
        {list}
        {dialog}
      </div>
    );
  }

  return (
    <div data-testid="referrals-list">
      <p className="text-[11px] text-[#6B7280] mb-3 leading-relaxed max-w-2xl">
        {unassigned
          ? 'Internships proposed by parents and mentors for the school in general, with no programme chosen. Assign a lead to the right programme and invite the company\'s representative — they confirm the details themselves before you approve the offer.'
          : 'Internships proposed by parents on behalf of a firm they work for or are connected to. Invite the company\'s representative — they confirm the details themselves before you approve the offer, and the parent stays credited on the offer.'}
      </p>
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {['all', 'submitted', 'invited', 'confirmed', 'approved', 'rejected'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${filter === f ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200 hover:border-[#9CA3AF]'}`} data-testid={`referral-filter-${f}`}>
            {f === 'all' ? `All (${rows.length})` : `${REFERRAL_LABEL[f]} (${rows.filter(r => r.status === f).length})`}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <Empty testId="referrals-empty" text={unassigned
          ? 'No school-level internship leads yet. Parents and mentors can propose a company from their Internships page at any time, with or without a programme.'
          : 'No parent proposals for this programme yet. Parents can propose a company from their Internships page while the programme is open.'} />
      ) : list}
      {dialog}
    </div>
  );
}

function ReferralDialog({ referral, decision, programs = [], onClose, onDone }) {
  const [note, setNote] = useState('');
  const [programId, setProgramId] = useState(referral.program_id || '');
  const [saving, setSaving] = useState(false);
  const needsProgram = decision === 'invite' && !referral.program_id;
  const openPrograms = programs.filter(p => p.status === 'open');
  const titles = {
    invite: needsProgram ? 'Assign this lead and invite the company' : 'Invite the company representative',
    request_changes: 'Ask the parent for more detail',
    reject: 'Decline this proposal',
  };
  const blurbs = {
    invite: `${referral.rep_name} will be emailed a secure link to set up a Partner account, review this internship and confirm it. You approve it afterwards.`,
    request_changes: 'The parent sees your note on their Internships page and can propose again.',
    reject: 'The parent sees your note on their Internships page. Nothing is sent to the company.',
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/coordinator/internship-referrals/${referral.id}/review`, {
        decision, note: note.trim() || null, program_id: programId || null,
      });
      toast.success(decision === 'invite' ? 'Invitation sent to the representative'
        : decision === 'reject' ? 'Proposal declined' : 'Note sent to the parent');
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save this decision'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="referral-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">{titles[decision]}</DialogTitle>
          <DialogDescription>{referral.company_name} · proposed by {referral.parent_name}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[#6B7280] leading-relaxed">{blurbs[decision]}</p>
        {needsProgram && (
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Internship programme *</Label>
            {openPrograms.length === 0 ? (
              <p className="text-[11px] text-amber-700 mt-1.5">
                No open programme to assign this lead to. Open a programme first.
              </p>
            ) : (
              <Select value={programId} onValueChange={setProgramId}>
                <SelectTrigger className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="referral-assign-program">
                  <SelectValue placeholder="Choose the programme" />
                </SelectTrigger>
                <SelectContent>
                  {openPrograms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
        <div>
          <Label className="text-sm font-medium text-[#1F2937]">
            Note{decision === 'invite' ? ' to the representative (optional)' : ' to the parent *'}
          </Label>
          <Textarea value={note} onChange={e => setNote(e.target.value)} className="mt-1.5 rounded-xl border-gray-200 min-h-[70px] resize-none" data-testid="referral-note" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={submit} disabled={saving || (needsProgram && !programId)} className={`rounded-xl font-semibold text-white ${decision === 'reject' ? 'bg-red-500 hover:bg-red-600' : 'bg-[#F97D6A] hover:bg-[#F06651]'} disabled:opacity-40`} data-testid="confirm-referral-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
